/**
 * Catalogos compartilhados da plataforma (P-FUN-1): UF, municipio, banco e CNAE.
 *
 * Dado publico de fonte oficial (IBGE), igual para toda empresa e so de leitura. O
 * formulario de endereco le daqui a lista de UF e a de municipios da UF escolhida.
 *
 * Reexportado por `../resources.ts`. Ver docs/FRONTEND.md, secao 3.
 */

import { api, requestPage } from '../client';

export interface CatalogState {
  /** Sigla, como o endereco grava. */
  code: string;
  ibge_code: string;
  /** Nome por extenso, com acento, como o IBGE publica. */
  name: string;
  region: string;
  region_name: string;
}

export interface CatalogCity {
  ibge_code: string;
  state_code: string;
  name: string;
}

/** Subclasse da CNAE 2.3 (P-FUN-1), procurada por texto ou codigo. */
export interface CatalogCnaeCode {
  code: string;
  formatted_code: string;
  description: string;
  section: string;
  section_name: string;
  division: string;
  division_name: string;
}

export const catalogs = {
  states: (signal?: AbortSignal) =>
    api.get<CatalogState[]>('/api/v1/catalogs/states?sort=name', signal),
  /** A lista inteira da UF, que cabe numa resposta (teto de 1.000). */
  cities: async (stateCode: string, signal?: AbortSignal) => {
    const pagina = await requestPage<CatalogCity>(
      `/api/v1/catalogs/states/${encodeURIComponent(stateCode)}/cities?sort=name&limit=1000`,
      signal,
    );
    return pagina.items;
  },
  /** CNAE pela busca (texto ou codigo, com ou sem pontuacao), na pagina comum. */
  cnaeCodes: async (busca: string, signal?: AbortSignal) => {
    const consulta = new URLSearchParams({ limit: '20' });
    if (busca.trim()) consulta.set('q', busca.trim());
    const pagina = await requestPage<CatalogCnaeCode>(
      `/api/v1/catalogs/cnae-codes?${consulta.toString()}`,
      signal,
    );
    return pagina.items;
  },
};

// --- Bancos e CNAE (P-TOM-C1) ------------------------------------------------

export interface CatalogBank {
  /** Codigo de compensacao de tres digitos, como a conta grava. */
  compe_code: string;
  ispb: string;
  name: string;
  legal_name: string;
  status: string;
  active: boolean;
}

export interface CatalogCnae {
  /** Sete digitos, como o cadastro grava. */
  code: string;
  /** Grafia da CONCLA, 0000-0/00. */
  formatted_code: string;
  description: string;
  section: string;
  section_name: string;
  division: string;
  division_name: string;
}

export const catalogBanks = {
  /** A relacao inteira do Banco Central, que cabe numa resposta (teto de 1.000). */
  list: async (signal?: AbortSignal) => {
    const pagina = await requestPage<CatalogBank>(
      '/api/v1/catalogs/bank-codes?sort=compe_code&limit=1000',
      signal,
    );
    return pagina.items;
  },
};

export const catalogCnaes = {
  /** Busca por codigo ou descricao, na pagina de 25 da plataforma. */
  search: async (texto: string, signal?: AbortSignal) => {
    const pagina = await requestPage<CatalogCnae>(
      `/api/v1/catalogs/cnae-codes?q=${encodeURIComponent(texto)}&limit=25`,
      signal,
    );
    return pagina.items;
  },
};
